import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import classNames from 'classnames';
import { useCallback } from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { usePageContext } from 'components/page/PageContext';
import {
  SortableContainer,
  SortableInfo,
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { IPublication } from 'features/publication/publicationAPI';
import { patchPublicationAsync } from 'features/publication/publicationSlice';
import {
  getFareyOrderByIndex,
  getFareyOrderUsingId,
  getFareyOrderUsingIndex,
} from 'helpers/farey-sorting';
import { AnyOrderObject, sameId } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';
import Publication from './Publication';

import { FormattedMessage } from 'react-intl';
import '../static/StaticPage.scss';
import './../page/Pages.scss';

import { postPublicationAsync } from 'features/publication/publicationSlice';

type PublicationListProps = {
  className?: string;
  asPage?: boolean;
  pageRef?: React.RefObject<HTMLDivElement>;
};

export default function PublicationList(props: PublicationListProps) {
  const { className, asPage = false, pageRef } = props;

  const { user, userIsManager } = useUser();
  const dispatch = useAppDispatch();
  const { publications } = useSite();
  const pageContext = usePageContext();

  const reorderPublications = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      const data: IPublication = {
        ...sourceItem,
      } as IPublication;
      const before = destinationItem
        ? ['top', 'left'].includes(
            extractClosestEdge(
              sortableInfo.location.current.dropTargets[0].data,
            ) ?? '',
          )
        : false;
      data.farey_order = getFareyOrderUsingId(
        sortByOrder(
          destinationList?.items?.filter(
            (item) => !item.pinned,
          ) as AnyOrderObject[],
        ),
        sameId(sourceList, destinationList) ? sourceItem.id : -1,
        destinationItem ? destinationItem.id : -1,
        before,
      );
      dispatch(patchPublicationAsync({ data: data })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              break;
            case 500:
            default:
              console.log(
                'Publication not updated due to internal error. Please reload the page.',
              );
              break;
          }
        },
      );
    },
    [dispatch],
  );

  function handleAddPublication(): void {
    const sortedPublications = sortByOrder(
      (publications ?? []) as AnyOrderObject[],
    );
    dispatch(
      postPublicationAsync({
        data: {
          title: '',
          text: '',
          farey_order: getFareyOrderUsingIndex(sortedPublications, -1, 0, true),
          publicationDate: new Date(),
          status: 'published',
          author: user,
        },
      }),
    );
  }

  const handleSortByDate = useCallback(() => {
    const sortedPublications = sortByOrder(
      (publications ?? []) as AnyOrderObject[],
    );
    const sortedByDate = [...sortedPublications].sort((a, b) => {
      const dateA = new Date(
        (a as IPublication).publicationDate ?? new Date(),
      ).getTime();
      const dateB = new Date(
        (b as IPublication).publicationDate ?? new Date(),
      ).getTime();
      return dateB - dateA;
    });
    sortedByDate.forEach((publication, index) => {
      const data: IPublication = {
        ...publication,
        farey_order: getFareyOrderByIndex(index),
      } as IPublication;
      dispatch(patchPublicationAsync({ data: data }));
    });
  }, [dispatch, publications]);

  const listInfo: SortableListInfo = {
    id: 1,
    listType: SortableListType.Publications,
    items: sortByOrder((publications ?? []) as AnyOrderObject[]),
  };
  const sorting = pageContext?.sorting ?? false;
  const sortableDisabled = !userIsManager || !sorting;
  const content = publications ? (
    <SortableContainer reorder={reorderPublications}>
      <SortableList
        className="page_sections publications"
        listInfo={listInfo}
        accepting={[SortableItemType.Publication]}
        disabled={sortableDisabled}
        containerRef={pageRef}
      >
        {asPage ? (
          <section>
            <h1 className="page_header">
              <FormattedMessage id={'PUBLICATIONS.PUBLICATIONS'} />
            </h1>
          </section>
        ) : null}
        <div className="list_menu">
          {user ? (
            <Icon
              symbol={IconSymbol.plus}
              onClick={handleAddPublication}
              size={20}
              hintProps={{ hint: { id: 'PUBLICATIONS.ADD_PUBLICATION' } }}
            />
          ) : null}
          {userIsManager ? (
            <Icon
              symbol={IconSymbol.sortByTime}
              size={20}
              hintProps={{ hint: { id: 'GENERAL.SORTING_HINT' } }}
              onClick={handleSortByDate}
              // style={{ transform: 'rotate(90deg)' }}
            />
          ) : null}
        </div>
        {listInfo.items?.map((publication) => (
          <SortableItem
            key={publication.id}
            listInfo={listInfo}
            item={publication as AnyOrderObject}
            itemType={SortableItemType.Publication}
            addHandle={false}
            disabled={sortableDisabled}
          >
            <Publication publication={publication} />
            {/* {publication.farey_order} */}
          </SortableItem>
        ))}
      </SortableList>
    </SortableContainer>
  ) : null;

  if (!asPage) {
    return content;
  }

  return (
    <div className={classNames('static_page', className)}>
      <div className="page_container">
        <div className="page_content" ref={pageRef}>
          {content}
        </div>
      </div>
    </div>
  );
}
